// src/utils/getWeightCategory.js

const WEIGHT_CATEGORIES = {
  Masculin: {
    "Mini-Poussin": [25, 35],
    Poussin: [25, 35],
    Pupille: [25, 30, 35, 40, 45, 50],
    Benjamin: [30, 35, 40, 45, 50, 55],
    Minime: [35, 40, 45, 50, 55, 60, 65],
    Cadet: [52, 57, 63, 70],
    Junior: [55, 61, 68, 76],
    Sénior: [60, 67, 75, 84],
    Espoir: [60, 67, 75, 84],

    "Vétéran 1": [67, 75, 84],
    "Vétéran 2": [67, 75, 84],
    "Vétéran 3": [67, 75, 84],
    "Vétéran 4": [67, 75, 84],
    "Vétéran 5": [67, 75, 84],
  },

  Féminin: {
    "Mini-Poussin": [25, 35],
    Poussin: [25, 35],
    Pupille: [25, 30, 35, 40, 45],
    Benjamin: [30, 35, 40, 45, 50],
    Minime: [35, 40, 45, 50, 55],
    Cadet: [47, 54, 61],
    Junior: [48, 53, 59, 66],
    Sénior: [50, 55, 61, 68],
    Espoir: [50, 55, 61, 68],

    "Vétéran 1": [55, 61, 68],
    "Vétéran 2": [55, 61, 68],
    "Vétéran 3": [55, 61, 68],
    "Vétéran 4": [55, 61, 68],
    "Vétéran 5": [55, 61, 68],
  },
};

const normalizeText = (value) => {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
};

const normalizeToken = (value) => {
  return normalizeText(value).replace(/[^a-z0-9]/g, "");
};

const CATEGORY_ALIASES = {
  minipoussin: "Mini-Poussin",
  minipoussins: "Mini-Poussin",

  poussin: "Poussin",
  poussins: "Poussin",

  pupille: "Pupille",
  pupilles: "Pupille",

  benjamin: "Benjamin",
  benjamins: "Benjamin",

  minime: "Minime",
  minimes: "Minime",

  cadet: "Cadet",
  cadets: "Cadet",

  junior: "Junior",
  juniors: "Junior",

  senior: "Sénior",
  seniors: "Sénior",
  seniores: "Sénior",

  espoir: "Espoir",
  espoirs: "Espoir",

  veteran1: "Vétéran 1",
  veterans1: "Vétéran 1",
  v1: "Vétéran 1",

  veteran2: "Vétéran 2",
  veterans2: "Vétéran 2",
  v2: "Vétéran 2",

  veteran3: "Vétéran 3",
  veterans3: "Vétéran 3",
  v3: "Vétéran 3",

  veteran4: "Vétéran 4",
  veterans4: "Vétéran 4",
  v4: "Vétéran 4",

  veteran5: "Vétéran 5",
  veterans5: "Vétéran 5",
  v5: "Vétéran 5",
};

const normalizeGender = (gender) => {
  const value = normalizeText(gender);

  if (value.startsWith("f") || value.includes("feminin")) {
    return "Féminin";
  }

  if (value.startsWith("m") || value.includes("masculin")) {
    return "Masculin";
  }

  return null;
};

const normalizeAgeCategory = (ageCategory) => {
  const token = normalizeToken(ageCategory);

  if (!token || token === "inconnu" || token === "horscategorie") {
    return null;
  }

  return CATEGORY_ALIASES[token] || ageCategory;
};

const parseWeight = (weight) => {
  if (weight === null || weight === undefined || weight === "") {
    return null;
  }

  const numericWeight = parseFloat(String(weight).replace(",", "."));

  if (!Number.isFinite(numericWeight) || Number.isNaN(numericWeight)) {
    return NaN;
  }

  return numericWeight;
};

const formatWeight = (weight) => {
  return Number.isInteger(weight)
    ? String(weight)
    : String(weight).replace(".", ",");
};

/**
 * Catégorie de poids Karaté.
 *
 * Convention conservée :
 * - "-35 kg" signifie poids strictement inférieur à 35 kg
 * - "+35 kg" signifie 35 kg et plus
 */
function getWeightCategory(gender, ageCategory, weight) {
  const numericWeight = parseWeight(weight);

  if (numericWeight === null || numericWeight <= 0) {
    return "Non pesé(e)";
  }

  if (Number.isNaN(numericWeight)) {
    return "Poids invalide";
  }

  const normalizedGender = normalizeGender(gender);

  if (!normalizedGender) {
    return "Genre inconnu";
  }

  const normalizedAgeCategory = normalizeAgeCategory(ageCategory);

  if (!normalizedAgeCategory) {
    return "Catégorie d'âge inconnue";
  }

  const thresholds = WEIGHT_CATEGORIES?.[normalizedGender]?.[normalizedAgeCategory];

  if (!thresholds) {
    console.error(
      `Catégorie de poids inconnue : ${normalizedGender} / ${normalizedAgeCategory}`
    );

    return "Catégorie inconnue";
  }

  const lastThreshold = thresholds[thresholds.length - 1];

  if (numericWeight >= lastThreshold) {
    return `+${formatWeight(lastThreshold)} kg`;
  }

  const selectedThreshold = thresholds.find(
    (threshold) => numericWeight < threshold
  );

  if (!selectedThreshold) {
    return "Aucune catégorie";
  }

  return `-${formatWeight(selectedThreshold)} kg`;
}

export default getWeightCategory;